import { memo, useEffect, useRef, useState } from "react";
import {
  Menu,
  MenuItem,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Stack,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import * as MuiIcons from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import c3crmLogo from "../../assests/crm_logo.png";
import { APP_ROUTES } from "../../config/appRoutes";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LogoutIcon from "@mui/icons-material/Logout";
import { clearToken } from "../../features/auth/authHelpers";
import { useDispatch, useSelector } from "react-redux";
import { setSelectUser } from "../../features/messages/messagesSlice";


// Brand colours (same green family as the dashboard)
const BRAND = {
  dark: "#0f5132",
  main: "#198754",
  light: "#45c57f",
  accent: "#fc8006",
};

// The C3CRM logo PNG (667x374) has wide built-in white margins: the artwork spans
// roughly x 17%–83% and y 31%–71%. Show the middle 75% of the width and 50% of the
// height, which keeps the whole artwork (incl. tagline) with a small safety margin.
const C3crmLogo = ({ height = 36 }) => {
  const imgH = height * 2; // visible window = 50% of image height
  const imgW = imgH * (667 / 374);
  return (
    <Box
      sx={{
        height,
        width: Math.round(imgW * 0.75), // visible window = 75% of image width
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <img src={c3crmLogo} alt="C3CRM" style={{ height: Math.round(imgH), width: Math.round(imgW), maxWidth: "none", flexShrink: 0 }} />
    </Box>
  );
};

// Just the "C3" mark from the logo (for the collapsed sidebar).
// Mark artwork sits at roughly x 17%–36%, y 30%–62% of the PNG.
const C3crmMark = ({ height = 34 }) => {
  const imgH = height / 0.36; // show ~36% of image height
  const imgW = imgH * (667 / 374);
  return (
    <Box sx={{ position: "relative", height, width: Math.round(imgW * 0.22), overflow: "hidden", flexShrink: 0 }}>
      <img
        src={c3crmLogo}
        alt="C3CRM"
        style={{
          position: "absolute",
          height: Math.round(imgH),
          width: Math.round(imgW),
          maxWidth: "none",
          left: -Math.round(imgW * 0.155),
          top: -Math.round(imgH * 0.285),
        }}
      />
    </Box>
  );
};

// Sidebar widths (desktop)
const MINI_WIDTH = 70;
const FULL_WIDTH = 248;

// Page area is memoised so sidebar hover/pin state changes don't re-render the
// current page (e.g. all dashboard charts) — that made opening/closing feel slow.
const PageContent = memo(({ fullBleed }) =>
  fullBleed ? (
    <Box mt={10} mb={5}>
      <Outlet />
    </Box>
  ) : (
    <Container maxWidth="xl">
      <Box mt={10} mb={5}>
        <Outlet />
      </Box>
    </Container>
  )
);

const Layout = styled(Box)({
  display: "flex",
  flexDirection: "column",
  // "clip" looks the same as "hidden" but cannot be scrolled by code (scrollIntoView etc.),
  // so pages can never be pushed up under the fixed top bar
  overflow: "clip",
});

// Sidebar groups, in display order
const NAV_GROUPS = ["Overview", "Engage", "CRM", "Admin"];

const navLinks = [
  { id: 1, path: `${APP_ROUTES.DASHBOARD}`, name: "Dashboard", icon: "Dashboard", group: "Overview" },
  { id: 2, path: `${APP_ROUTES.QRCODE.LIST}`, name: "QR Code", icon: "QrCode", group: "Engage" },
  { id: 3, path: `${APP_ROUTES.MESSAGES}`, name: "Messages", icon: "Message", group: "Engage" },
  { id: 4, path: `${APP_ROUTES.CAMPAIGN.LIST}`, name: "OutBound Campaign", icon: "Campaign", group: "Engage" },
  { id: 5, path: `${APP_ROUTES.TEMPLATE.LIST}`, name: "Templates", icon: "Description", group: "Engage" },
  { id: 6, path: `${APP_ROUTES.CHATBOT.LIST}`, name: "Chat Bot", icon: "SmartToy", group: "Engage" },
  { id: 7, path: `${APP_ROUTES.LEADMANAGEMENT.MY_TASK}`, name: "Lead Management", icon: "PeopleAlt", group: "CRM" },
  { id: 9, path: `${APP_ROUTES.CONTACT.LIST}`, name: "Contacts", icon: "Contacts", group: "CRM" },
  { id: 10, path: `${APP_ROUTES.FILES.LIST}`, name: "Files", icon: "Folder", group: "Admin" },
  { id: 11, path: `${APP_ROUTES.EMPLOYEE.LIST}`, name: "Employees", icon: "PersonAdd", group: "Admin" },
  { id: 12, path: `${APP_ROUTES.ORGANIZATION.LIST}`, name: "Organization", icon: "Business", group: "Admin" },
  // Quotation hidden from the side navbar (route /quotation-list still works)
  // { id: 13, path: `${APP_ROUTES.QUOTATION.LIST}`, name: "Quotation", icon: "RequestQuote", group: "CRM" },
  { id: 14, path: `${APP_ROUTES.APPOINTMENTS.LIST}`, name: "Appointment", icon: "BookOnline", group: "CRM" },
];


const NavBar = () => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authentication);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:600px)");

  // Desktop sidebar: collapsed to icons by default. Hover or the menu button opens it
  // as an overlay on top of the page — the page itself never moves.
  const [hovered, setHovered] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const [toggled, setToggled] = useState(false);
  const paperRef = useRef(null);
  const menuBtnRef = useRef(null);
  const toggleSidebar = () => setToggled((t) => !t);
  const closeSidebar = () => {
    setToggled(false);
    setHovered(false);
  };
  const expanded = isMobile || toggled || hovered; // show labels?
  const contentOffset = isMobile ? 0 : MINI_WIDTH;

  // Click anywhere outside the sidebar closes a toggled-open sidebar
  useEffect(() => {
    if (!toggled || isMobile) return undefined;
    const onDown = (e) => {
      if (paperRef.current?.contains(e.target) || menuBtnRef.current?.contains(e.target)) return;
      setToggled(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [toggled, isMobile]);

  const moduleAccess = user?.module_access || {};
  console.log("moduleAccess",moduleAccess);
  
  const navModuleMap = {
    [APP_ROUTES.QRCODE.LIST]: "qrGeneration",
    [APP_ROUTES.MESSAGES]: "messages",
    [APP_ROUTES.CAMPAIGN.LIST]: "outboundCampaign",
    [APP_ROUTES.TEMPLATE.LIST]: "templates",
    [APP_ROUTES.CHATBOT.LIST]: "chatbot",
    [APP_ROUTES.LEADMANAGEMENT.MY_TASK]: "leadGeneration",
    [APP_ROUTES.CONTACT.LIST]: "contacts",
    [APP_ROUTES.FILES.LIST]: "filecategory",
    [APP_ROUTES.EMPLOYEE.LIST]: "employeeCreation",
    [APP_ROUTES.ORGANIZATION.LIST]: "department",
    [APP_ROUTES.QUOTATION.LIST]: "quotationCreation",
    [APP_ROUTES.APPOINTMENTS.LIST]: "slot_booking",
  };

  const hasModuleAccess = (key) => {
    if (!key) return true;
    const val = moduleAccess[key];
    return val === true || val === "true" || val === 1 || val === "1";
  };

  const visibleNavLinks = navLinks.filter((l) => {
    const required = navModuleMap[l.path];
    return hasModuleAccess(required);
  });

  const toggleDrawer = (status) => () => setOpen(status);

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
    setToggled(false);
    dispatch(setSelectUser(null));
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    navigate(APP_ROUTES.LOGIN);
    dispatch({ type: "RESET_STORE" });
    clearToken();
    handleClose();
  };

  // Base path segment, used to highlight the current page in the sidebar
  const getBase = (p) => (p?.split("/")[1] || "").split("-")[0] || "";
  const currentBase = getBase(location.pathname);

  const displayName = user?.username ?? "User";
  const initial = String(displayName).charAt(0).toUpperCase();
  const today = now.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  const timeNow = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  return (
    <Layout>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: "62px",
          // Sit to the right of the desktop sidebar
          left: contentOffset,
          width: `calc(100% - ${contentOffset}px)`,
          // No transition here: animating the offset re-lays-out the whole page every frame (slow)
          color: "#0f172a",
          background: "linear-gradient(90deg, rgba(255,255,255,0.94) 0%, rgba(240,253,244,0.92) 55%, rgba(238,242,255,0.92) 100%)",
          backdropFilter: "saturate(180%) blur(12px)",
          borderBottom: "1px solid #e9edf2",
          boxShadow: "0 4px 20px -12px rgba(15,23,42,0.25)",
          // Thin brand gradient line along the very top
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${BRAND.dark}, ${BRAND.main}, ${BRAND.light}, ${BRAND.accent})`,
          },
        }}
      >
        <Toolbar sx={{ minHeight: "62px !important", gap: 1.5, px: { xs: 1.5, md: 3 } }}>
          {/* Menu button: opens drawer on mobile, pins/unpins sidebar on desktop */}
          <IconButton
            ref={menuBtnRef}
            onClick={isMobile ? toggleDrawer(true) : toggleSidebar}
            aria-label={isMobile ? "menu" : toggled ? "close sidebar" : "open sidebar"}
            title={isMobile ? "Menu" : toggled ? "Close menu" : "Open menu"}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              color: BRAND.main,
              bgcolor: `${BRAND.main}12`,
              border: `1px solid ${BRAND.main}26`,
              transition: "all .2s ease",
              "&:hover": { bgcolor: BRAND.main, color: "#fff" },
            }}
          >
            {!isMobile && toggled ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>

          {/* C3CRM logo only on mobile (desktop sidebar already shows it) */}
          {isMobile && (
            <Box
              onClick={() => handleNavigate(APP_ROUTES.DASHBOARD)}
              title="Go to dashboard"
              sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <C3crmLogo height={36} />
            </Box>
          )}

          {/* Client brand: logo tile + name */}
          {!isMobile && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ pl: 0.5, minWidth: 0 }}
            >
              <Box
                sx={{
                  p: "2px",
                  flexShrink: 0,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.light}, ${BRAND.accent})`,
                  boxShadow: `0 4px 12px -6px ${BRAND.main}`,
                }}
              >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2.5,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#fff",
                }}
              >
                {user?.logo ? (
                  <img src={user.logo} alt="Client Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (
                  <Typography fontWeight={800} sx={{ color: BRAND.main }}>
                    {String(user?.client_name || "C").charAt(0).toUpperCase()}
                  </Typography>
                )}
              </Box>
              </Box>
              <Box sx={{ minWidth: 0, display: { xs: "none", md: "block" } }}>
                <Typography variant="body2" fontWeight={800} color="#0f172a" noWrap lineHeight={1.25} sx={{ maxWidth: 220 }}>
                  {user?.client_name}
                </Typography>
              </Box>
            </Stack>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Date & time pill */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              display: { xs: "none", sm: "flex" },
              pl: 0.5,
              pr: 1.5,
              py: 0.5,
              borderRadius: 5,
              bgcolor: "#fff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 6px -4px rgba(15,23,42,0.2)",
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.light})`,
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 14 }} />
            </Box>
            <Box sx={{ lineHeight: 1 }}>
              <Typography variant="caption" fontWeight={700} color="#334155" display="block" lineHeight={1.2}>
                {today}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2} sx={{ fontSize: 11 }}>
                {timeNow}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: { xs: "none", sm: "block" }, width: "1px", height: 28, bgcolor: "#e2e8f0" }} />

          {/* User button */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={handleClick}
            sx={{
              cursor: "pointer",
              pl: 0.5,
              pr: 1,
              py: 0.5,
              borderRadius: 5,
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
              transition: "all .2s ease",
              "&:hover": { borderColor: `${BRAND.main}66`, boxShadow: `0 4px 12px -6px ${BRAND.main}` },
            }}
          >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 800,
                  color: "#fff",
                  background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.light})`,
                }}
              >
                {initial}
              </Avatar>
              <Box
                title="Online"
                sx={{
                  position: "absolute",
                  right: -1,
                  bottom: -1,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: "#22c55e",
                  border: "2px solid #fff",
                }}
              />
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1 }}>
              <Typography variant="body2" fontWeight={700} lineHeight={1.2} color="#0f172a" noWrap sx={{ maxWidth: 140 }}>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" lineHeight={1.2} noWrap sx={{ maxWidth: 140, display: "block" }}>
                My account
              </Typography>
            </Box>
            <KeyboardArrowDownIcon
              sx={{ fontSize: 18, color: "#64748b", transition: "transform .2s ease", transform: openMenu ? "rotate(180deg)" : "none" }}
            />
          </Stack>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  width: 230,
                  p: 0.75,
                  borderRadius: 3,
                  border: "1px solid #e9edf2",
                  boxShadow: "0 16px 40px -16px rgba(15,23,42,0.35)",
                },
              },
              list: { sx: { p: 0 } },
            }}
          >
            <MenuItem
              onClick={handleLogout}
              sx={{
                gap: 1.25,
                px: 1,
                py: 0.9,
                borderRadius: 2,
                "&:hover": { bgcolor: "#dc26260f" },
                "&:hover .menu-icon": { bgcolor: "#dc2626", color: "#fff" },
              }}
            >
              <Box
                className="menu-icon"
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#dc2626",
                  bgcolor: "#dc262614",
                  transition: "all .15s ease",
                }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} lineHeight={1.3} sx={{ color: "#dc2626" }}>
                  Logout
                </Typography>
                <Typography variant="caption" color="text.secondary" lineHeight={1.3} display="block">
                  Sign out of your account
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer Sidebar: permanent mini rail on desktop (expands on hover), temporary on mobile */}
      <Drawer
        anchor="left"
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={toggleDrawer(false)}
        PaperProps={{
          ref: paperRef,
          onMouseEnter: isMobile ? undefined : () => setHovered(true),
          onMouseLeave: isMobile ? undefined : () => setHovered(false),
          sx: {
            width: isMobile ? FULL_WIDTH : expanded ? FULL_WIDTH : MINI_WIDTH,
            overflowX: "hidden",
            transition: "width .18s cubic-bezier(.4,0,.2,1), box-shadow .18s ease",
            willChange: "width",
            color: "#e2e8f0",
            border: "none",
            display: "flex",
            flexDirection: "column",
            background: `radial-gradient(circle at 0% 0%, rgba(99,102,241,0.18) 0, transparent 40%),
              radial-gradient(circle at 100% 100%, rgba(99,102,241,0.12) 0, transparent 45%),
              linear-gradient(180deg, #0f172a 0%, #111c33 60%, #0f172a 100%)`,
            // Floating shadow while expanded over the page
            boxShadow: !isMobile && expanded ? "12px 0 32px -12px rgba(15,23,42,0.55)" : "none",
            // Hide labels while collapsed (kept in the DOM so nothing jumps)
            "& .nav-label": {
              opacity: expanded ? 1 : 0,
              transition: "opacity .12s ease",
              whiteSpace: "nowrap",
            },
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: expanded ? 1.5 : `${(MINI_WIDTH - 52) / 2}px`, pt: 1.75, pb: 1.5, minHeight: 88 }}>
          <Box
            onClick={() => handleNavigate(APP_ROUTES.DASHBOARD)}
            title="Go to dashboard"
            sx={{
              flex: 1,
              minWidth: 0,
              height: expanded ? 58 : 52,
              px: expanded ? 1.5 : 0,
              borderRadius: 2.5,
              bgcolor: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 20px -10px rgba(0,0,0,0.6)",
              transition: "height .18s ease",
            }}
          >
            {expanded ? <C3crmLogo height={50} /> : <C3crmMark height={30} />}
          </Box>
          {expanded && (
            <IconButton
              onClick={isMobile ? toggleDrawer(false) : closeSidebar}
              aria-label="close menu"
              title="Close menu"
              sx={{
                flexShrink: 0,
                width: 34,
                height: 34,
                borderRadius: 2,
                color: "#94a3b8",
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.14)", color: "#fff" },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Divider line below the logo */}
        <Box
          sx={{
            mx: 1.5,
            mb: 0.5,
            height: "1px",
            flexShrink: 0,
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)",
          }}
        />

        <List
          sx={{
            // collapsed: (MINI_WIDTH 70 - 52px button) / 2 = 9px each side, so icons are centred
            px: expanded ? 1.5 : `${(MINI_WIDTH - 52) / 2}px`,
            py: 0,
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            // Thin dark scrollbar when expanded; hidden when collapsed so icons stay centred
            scrollbarWidth: expanded ? "thin" : "none",
            scrollbarColor: "rgba(255,255,255,0.15) transparent",
            "&::-webkit-scrollbar": { width: expanded ? 6 : 0 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.15)", borderRadius: 3 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          }}
        >
          {NAV_GROUPS.map((group) => {
            const links = visibleNavLinks.filter((l) => l.group === group);
            if (!links.length) return null;
            return (
              <Box key={group} sx={{ mb: 0.5 }}>
                {/* Group heading when expanded; short divider when collapsed */}
                {expanded ? (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      px: 1.5,
                      pt: 1,
                      pb: 0.75,
                      color: "#64748b",
                      fontSize: 10.5,
                      fontWeight: 800,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group}
                  </Typography>
                ) : (
                  <Box sx={{ mx: "auto", my: 1.1, width: 24, height: "2px", borderRadius: 1, bgcolor: "rgba(255,255,255,0.12)" }} />
                )}

                {links.map(({ id, path, name, icon }) => {
                  const Icon = MuiIcons[icon] || MuiIcons.HelpOutline;
                  const color = "#cbd5e1"; // neutral icon colour (no green) for all modules
                  const isSelected = currentBase === getBase(path);

                  return (
                    <ListItem key={id} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleNavigate(path)}
                        title={expanded ? undefined : name}
                        sx={{
                          position: "relative",
                          py: 0.75,
                          px: "11px", // (52px button - 30px icon) / 2 → icon centred when collapsed
                          borderRadius: 2.5,
                          color: "#cbd5e1",
                          transition: "all .2s ease",
                          // Glowing marker on the sidebar edge for the current page
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: expanded ? -12 : -(MINI_WIDTH - 52) / 2,
                            top: "22%",
                            bottom: "22%",
                            width: 4,
                            borderRadius: "0 4px 4px 0",
                            bgcolor: BRAND.light,
                            boxShadow: `0 0 10px ${BRAND.light}`,
                            opacity: isSelected ? 1 : 0,
                            transition: "opacity .2s ease",
                          },
                          "& .nav-icon": {
                            width: 30,
                            height: 30,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color,
                            bgcolor: "rgba(255,255,255,0.06)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            transition: "all .2s ease",
                          },
                          "& .nav-label span": { transition: "transform .2s ease", display: "inline-block" },
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.06)",
                            color: "#fff",
                            "& .nav-icon": { color: "#fff", bgcolor: "rgba(255,255,255,0.14)", borderColor: "rgba(255,255,255,0.2)" },
                            "& .nav-label span": { transform: "translateX(3px)" },
                          },
                          "&.Mui-selected, &.Mui-selected:hover": {
                            color: "#fff",
                            background: `linear-gradient(90deg, ${BRAND.main}, ${BRAND.main}99)`,
                            boxShadow: `0 8px 18px -10px ${BRAND.light}`,
                            "& .nav-icon": { color: "#fff", bgcolor: "rgba(255,255,255,0.18)", borderColor: "rgba(255,255,255,0.25)" },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 42 }}>
                          <Box className="nav-icon">
                            <Icon sx={{ fontSize: 18 }} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          className="nav-label"
                          primary={name}
                          sx={{ my: 0, "& span": { fontSize: 14, fontWeight: isSelected ? 700 : 500 } }}
                        />
                        {isSelected && <Box className="nav-label" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }} />}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </Box>
            );
          })}
        </List>

        {/* User footer */}
        <Box sx={{ py: 1.5, px: expanded ? 1.5 : `${(MINI_WIDTH - 52) / 2}px`, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ py: 1, px: "9px", borderRadius: 2.5, bgcolor: "rgba(255,255,255,0.05)" }}>
            <Avatar
              title={expanded ? undefined : displayName}
              sx={{ width: 34, height: 34, flexShrink: 0, fontWeight: 800, color: "#fff", background: `linear-gradient(135deg, ${BRAND.main}, ${BRAND.light})` }}
            >
              {initial}
            </Avatar>
            <Box className="nav-label" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} color="#fff" noWrap>
                {displayName}
              </Typography>
              <Typography variant="caption" color="#94a3b8" noWrap display="block">
                {user?.client_name}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Drawer>

      {/* Main Content (offset by the desktop sidebar width) */}
      <Box component="main" sx={{ ml: `${contentOffset}px` }}>
        <PageContent fullBleed={isMobile && location.pathname === "/messages"} />
      </Box>
    </Layout>
  );
};

export default NavBar;
